import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getContent, type Content } from '../data/content';
import { useWallet } from '../lib/wallet';
import { UnlockSheet } from '../components/UnlockSheet';
import { geneUrl } from '../lib/memegenes';
import { GeneCheckIcon } from '../components/GeneCheckIcon';
import { LockIcon } from '../components/LockIcon';
import { cents } from '../lib/format';

/**
 * Content Detail (/p/:id) — an article, photo, or video with the
 * preview → unlock → reveal flow over its locked body. Backing the creator is
 * the action; once settled the full work shows in place and stays unlocked
 * (localStorage). Carries the Verified-by-MemeGenes link like other detail pages.
 */
export function ContentDetail() {
  const { id } = useParams();
  const item = id ? getContent(id) : undefined;
  const { isUnlocked } = useWallet();
  const [sheetOpen, setSheetOpen] = useState(false);
  // Drives the reveal transition the instant unlock settles, independent of
  // the persisted flag (which is already true by the time onUnlocked fires).
  const [justRevealed, setJustRevealed] = useState(false);

  if (!item) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Not found</h1>
        <p className="mt-3 text-provny-muted">This piece may have moved or never existed.</p>
        <Link to="/explore" className="mt-6 inline-block font-semibold text-provny-cyber-dim hover:underline dark:text-provny-cyber">
          ← Back to Explore
        </Link>
      </main>
    );
  }

  const unlocked = isUnlocked(item.id) || justRevealed;

  return (
    <main className="mx-auto max-w-3xl px-6 py-8">
      <Link to="/explore" className="text-sm text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white">
        ← Explore
      </Link>

      <header className="mt-6">
        <Link
          to={`/c/${item.channelSlug}`}
          className="text-xs font-medium uppercase tracking-wider text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white"
        >
          {item.channel}
        </Link>
        <h1 className="mt-2 font-heading text-4xl font-black leading-tight tracking-tight">{item.title}</h1>
        <Link
          to={`/@${item.creator}`}
          className="mt-2 inline-block text-provny-muted transition-colors hover:text-provny-black dark:hover:text-provny-white"
        >
          @{item.creator}
        </Link>
      </header>

      <div className="mt-8">
        {item.type === 'article' && <ArticleBody item={item} unlocked={unlocked} reveal={justRevealed} />}
        {item.type === 'photo' && <PhotoBody item={item} unlocked={unlocked} />}
        {item.type === 'video' && <VideoBody item={item} unlocked={unlocked} />}
      </div>

      {!unlocked && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="w-full rounded-xl bg-provny-cyber py-3 font-semibold text-provny-ink transition hover:bg-provny-cyber/90 sm:w-auto sm:px-8"
          >
            Back this · {cents(item.priceCents)}
          </button>
          <p className="mt-2 text-sm text-provny-muted">A tiny amount, straight to @{item.creator}. Then it's yours.</p>
        </div>
      )}

      {/* Provenance — MemeGenes present, not invisible */}
      <a
        href={geneUrl(item.geneId)}
        target="_blank"
        rel="noopener noreferrer"
        className="group mt-10 flex items-center gap-4 rounded-2xl border border-provny-border p-5 transition-colors hover:border-provny-gold dark:border-provny-border-dark"
      >
        <GeneCheckIcon size={28} />
        <div className="flex-1">
          <p className="font-semibold">Verified original</p>
          <p className="text-sm text-provny-muted">
            Invisible proof of origin, embedded at publish. View this original's Gene on MemeGenes.
          </p>
        </div>
        <span className="font-mono text-sm text-provny-muted transition-colors group-hover:text-provny-gold">
          {item.geneId} →
        </span>
      </a>

      {sheetOpen && (
        <UnlockSheet
          content={item}
          onClose={() => setSheetOpen(false)}
          onUnlocked={() => {
            setJustRevealed(true);
            setSheetOpen(false);
          }}
        />
      )}
    </main>
  );
}

/** Article: free dek + lede, then the body fades up from a blur on unlock. */
function ArticleBody({ item, unlocked, reveal }: { item: Content; unlocked: boolean; reveal: boolean }) {
  if (item.preview.type !== 'article' || item.body.type !== 'article') return null;

  if (!unlocked) {
    return (
      <article>
        <p className="font-heading text-xl font-medium leading-snug text-provny-black/90 dark:text-provny-white/90">
          {item.preview.dek}
        </p>
        <div className="relative mt-6">
          <p className="text-lg leading-relaxed text-provny-black/80 dark:text-provny-white/80">{item.preview.lede}</p>
          {/* fade the cut-off into the page background — the lock affordance */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-provny-white to-transparent dark:from-provny-ink" />
        </div>
      </article>
    );
  }

  return (
    <article className={reveal ? 'motion-safe:animate-[fadeIn_0.6s_ease]' : ''}>
      <p className="font-heading text-xl font-medium leading-snug text-provny-black/90 dark:text-provny-white/90">
        {item.preview.dek}
      </p>
      <div className="mt-6 flex flex-col gap-4 text-lg leading-relaxed text-provny-black/80 dark:text-provny-white/80">
        {item.body.markdown.split('\n\n').map((para, i) => (
          <Paragraph key={i} text={para} />
        ))}
      </div>
      {item.body.image && (
        <img src={item.body.image} alt={item.title} className="mt-8 w-full rounded-2xl object-cover" />
      )}
    </article>
  );
}

/** Render **bold** spans inside an otherwise plain paragraph. */
function Paragraph({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} className="font-semibold text-provny-black dark:text-provny-white">
            {p.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </p>
  );
}

/** Photo: blurred preview that resolves to full-res on unlock. */
function PhotoBody({ item, unlocked }: { item: Content; unlocked: boolean }) {
  if (item.preview.type !== 'photo' || item.body.type !== 'photo') return null;
  return (
    <figure className="overflow-hidden rounded-2xl border border-provny-border dark:border-provny-border-dark">
      <img
        src={unlocked ? item.body.image : item.preview.image}
        alt={item.title}
        className={`w-full object-cover transition-[filter,transform] duration-700 ${
          unlocked ? 'blur-0' : 'blur-2xl scale-105'
        }`}
      />
    </figure>
  );
}

/** Video: poster + duration with a lock; a mock play state once unlocked. */
function VideoBody({ item, unlocked }: { item: Content; unlocked: boolean }) {
  const [playing, setPlaying] = useState(false);
  if (item.preview.type !== 'video' || item.body.type !== 'video') return null;

  return (
    <figure className="relative overflow-hidden rounded-2xl border border-provny-border dark:border-provny-border-dark">
      <img
        src={item.body.poster}
        alt={item.title}
        className={`aspect-video w-full object-cover transition duration-700 ${unlocked ? '' : 'blur-xl scale-105'}`}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        {unlocked ? (
          <button
            type="button"
            onClick={() => setPlaying((p) => !p)}
            aria-label={playing ? 'Pause' : 'Play'}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-provny-white/90 text-provny-ink shadow-lg transition hover:scale-105"
          >
            {playing ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
        ) : (
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-provny-ink/55 text-white/90 backdrop-blur">
            <LockIcon size={22} />
          </span>
        )}
      </div>
      <figcaption className="absolute bottom-3 right-3 rounded-full bg-provny-ink/70 px-2 py-0.5 font-mono text-xs text-white/90 backdrop-blur">
        {unlocked && playing ? 'Playing…' : item.body.duration}
      </figcaption>
    </figure>
  );
}
